import React, {ReactElement, useState} from "react";
import {Box, styled} from "@mui/material";
import CareerProjectAccordion from "./CareerProjectAccordion";
import {ICareerSection} from "../../types/ICareerProject";
import DescriptionText from "../typography/DescriptionText";
import {RootState} from "../../redux/store";
import {connect} from "react-redux";
import {ILanguageTokens} from "../../localization/LanguageTokens";

const Root = styled(Box)(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
}));

const TimelineBox = styled(Box)(({theme}) => ({
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.only('xs')]: {
        padding: theme.spacing(0.5),
    },
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(2),
    },

}));

const RedBubble = styled(Box)(({theme}) => ({
    ...theme.typography.h5,
    color: theme.palette.text.secondary,

    [theme.breakpoints.only('xs')]: {
        width: theme.spacing(6),
        height: theme.spacing(4.5),
        marginLeft: `-${theme.spacing(0)}`,
        fontSize: 16,
    },
    [theme.breakpoints.up('sm')]: {
        width: theme.spacing(10),
        height: theme.spacing(4.5),
        marginLeft: `-${theme.spacing(2.5)}`,
        fontSize: 24,
    },
    borderRadius: 8,
    backgroundColor: theme.palette.primary.main,

    alignContent: 'center',
    textAlign: 'center',
    zIndex: 5,
    fontSize: 60
}));

const TimelineSection = styled(Box)(({theme}) => ({
    position: 'relative',
    [theme.breakpoints.only('xs')]: {
        marginLeft: theme.spacing(2.5),
    },
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(2),
    },
}));


const RedLine = styled(Box)(({theme}) => ({
    position: 'absolute',
    backgroundColor: theme.palette.primary.main,
    height: '100%',
    width: theme.spacing(1),
}));

const CareerProjectBox = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    marginLeft: `-${theme.spacing(1)}`,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));


const RedDot = styled(Box)(({theme}) => ({
    minWidth: 24,
    minHeight: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    position: 'relative',
    zIndex: 1,
}));


interface IProps {
    careerSections: ICareerSection[];
    languageTokens: ILanguageTokens;
}

interface IState {
    expandedProjectHeader: string;
    previousExpandedProjectHeader: string;
}

function VerticalTimeline({careerSections, languageTokens}: IProps): ReactElement {
    const [state, setState] = useState<IState>({
        expandedProjectHeader: careerSections[0].careerProjects[0].header,
        previousExpandedProjectHeader: '',
    });
    const {expandedProjectHeader, previousExpandedProjectHeader} = state;

    const handleSetExpandedProjectHeader = (newExpandedProjectHeader: string) => {
        setState({
            ...state,
            previousExpandedProjectHeader: expandedProjectHeader,
            expandedProjectHeader: newExpandedProjectHeader
        });
    }

    return (
        <Root id={'VerticalTimeline'} maxWidth={'xl'}>
            <DescriptionText text={languageTokens.careerProjectsDescription}/>

            <TimelineBox>

                {careerSections.map(({year, careerProjects}, i) => {
                    return <React.Fragment key={i}>
                        <RedBubble
                            key={i}
                            id={'RedBubble'}
                        >
                            {year}
                        </RedBubble>
                        {
                            careerProjects.map((project, j) => {
                                const isExpanded = project.header === expandedProjectHeader;
                                const wasExpanded = project.header === previousExpandedProjectHeader;
                                return <TimelineSection
                                    id={'ProjectBox'}
                                    key={j}
                                >
                                    <RedLine id={`VerticalLine_${j}`}/>

                                    <CareerProjectBox id={`CareerProject_${j}`}>
                                        <RedDot id={`RedDot_${j}`}/>

                                        <CareerProjectAccordion
                                            careerProject={project}
                                            isExpanded={isExpanded}
                                            wasExpanded={wasExpanded}
                                            onToggle={() => handleSetExpandedProjectHeader(isExpanded ? '' : project.header)}
                                        />

                                    </CareerProjectBox>
                                </TimelineSection>
                            })
                        }
                    </React.Fragment>
                })}
            </TimelineBox>
        </Root>
    );
}

const mapStateToProps = (state: RootState) => ({
    languageTokens: state.language.languageTokens,
});

const mapActionsToProps = {};

export default connect(mapStateToProps, mapActionsToProps)(VerticalTimeline);

